import {
    BonusType,
    Locale,
} from '@superbet-group/web.types';
import Vue from 'vue';

import {
    BonusCategoryDto,
    BonusSubCategory,
} from '@core/mappers/user/dtos/BalanceDto';
import BonusCategory from '@core/models/bonus/BonusCategory';
import {
    BonusConditions,
    BonusEventType,
    BonusTicketType,
    ConditionDetail,
} from '@core/models/bonus/BonusConditions';
import { BonusStatus } from '@core/models/bonus/BonusStatus';
import { BonusSubtype } from '@core/models/bonus/BonusSubtype';

import {
    BonusDetailData,
    BonusDetailsItem,
    BonusFieldKey,
    BonusRequirementParams,
    BonusRequirement,
    BonusRequirementKey,
    MAX_DESCRIPTION_ITEMS_NUMBER,
} from './types';
import { BonusKind } from '@core/models/bonus/Bonus';
import { Condition } from '@app/promotionHub/shared/types/Condition';
import { BonusCustomContentConditions } from '@app/promotionHub/shared/types/CustomContent';
import BonusDescription from '@core/models/bonus/BonusDescription';
import {
    isCasinoBonus,
    isFreeSpinBonus,
    isSportOrFreeBetBonus,
    isWageringBonus,
    isSportWageringBonus,
} from '@app/promotionHub/bonuses/utils/bonusTypeHelpers';
import { getConfig } from '@superbet-group/web.lib.config';
import { PromotionBonusType } from '../../promotions/types/Promotion';
import { useI18n } from '@superbet-group/web.lib.vue-utilities';
import { translationPlaceholderFormat } from '@superbet-group/web.lib.utilities';
import { isValidUrl } from '@src/app/utils/domainHelpers';

export function isFreeBet(subCategory: BonusCategoryDto): boolean {
    return subCategory === BonusSubCategory.FreeBet;
}

export function isProgressBarVisible(category: BonusCategory, subCategory: BonusCategoryDto): boolean {
    const categoryArray = [
        BonusCategory.Active,
        BonusCategory.AvailableToWager,
        BonusCategory.History,
    ];

    return categoryArray.includes(category) && !isFreeBet(subCategory);
}

export function isWageInfoVisible(category: BonusCategory, subCategory: BonusCategoryDto): boolean {
    const categoryArray = [
        BonusCategory.Active,
        BonusCategory.AvailableToWager,
        BonusCategory.UnavailableToWager,
    ];

    return categoryArray.includes(category) && !isFreeBet(subCategory);
}

export function isStatusInfoVisible(status: BonusStatus): boolean {
    const visibleStatuses: BonusStatus[] = [
        BonusStatus.Redeemed,
        BonusStatus.Lost,
        BonusStatus.Expired,
        BonusStatus.Declined,
    ];
    return visibleStatuses.includes(status);
}

export function isAmountRedeemedVisible(status: BonusStatus, subCategory: BonusCategoryDto): boolean {
    return (isFreeBet(subCategory) && status === BonusStatus.Active)
            || (status !== BonusStatus.Active && status !== BonusStatus.Pending && status !== BonusStatus.Declined);
}

export function isWageredAmountVisible(status: BonusStatus, subCategory: BonusCategoryDto): boolean {
    return !isFreeBet(subCategory)
        && status !== BonusStatus.Declined;
}

export function getBonusDescription(type: BonusType, subtype: BonusSubtype): string | undefined {
    switch (type) {
        case BonusType.Sport:
            return subtype === BonusSubtype.NonWagering ? 'bonus.active_card.description_no_wagering' : 'bonus.active_card.description_wagering_sport';
        case BonusType.Freebet:
            return 'bonus.active_card.description_free_bet';
        case BonusType.FreeSpins:
            return 'bonus.active_card.description_free_spins';
        case BonusType.Casino:
            return 'bonus.active_card.description_wagering_casino';
        case BonusType.Virtuals:
        case BonusType.CasinoVirtuals:
            return 'bonus.active_card.description_virtuals';
        case BonusType.Bingo:
            return 'bonus.active_card.description_bingo';
        case BonusType.LiveDealer:
            return 'bonus.active_card.description_live_dealer';
        default:
            return undefined;
    }
}

// Detail item should be visible if it is positive number or if item is PotentialWin
function isDetailFieldVisible(item: BonusDetailsItem) {
    if (item.key === BonusFieldKey.PotentialWin || item.key === BonusFieldKey.AmountWon) {
        return true;
    } else {
        return typeof item.value === 'string'
            ? parseFloat(item.value.replace(',', '.')) > 0
            : item.value;
    }
}

export function getBonusDetailsList({
    kind,
    type,
    amountLeft,
    bonusUsedAmount,
    redeemedAmount,
    bonusAwarded,
    bonusPotentialWin,
    pendingAmount,
    bonusGainedAmount,
}: BonusDetailData): BonusDetailsItem[] {
    const { formatCurrency } = useI18n();
    return [
        {
            key: BonusFieldKey.PendingAmount,
            text: 'bonus.card.details_wager_pending',
            value: formatCurrency(pendingAmount),
            isCurrencyVisible: true,
        },
        {
            key: BonusFieldKey.AvailableBonusAmount,
            text: 'bonus.card.details_amount_available',
            value: formatCurrency(amountLeft),
            isCurrencyVisible: true,
        },
        {
            key: BonusFieldKey.InitialAmount,
            text: 'bonus.card.details_initial_amount',
            value: formatCurrency(bonusAwarded),
            isCurrencyVisible: true,
        },
        {
            key: BonusFieldKey.AvailableFreeSpins,
            text: 'bonus.card.details_spins_available',
            value: kind === BonusKind.Betler ? amountLeft : bonusAwarded,
            isCurrencyVisible: false,
        },
        {
            key: BonusFieldKey.AwardedFreeSpins,
            text: 'bonus.card.details_spins_awarded',
            value: bonusAwarded,
            isCurrencyVisible: false,
        },
        {
            key: BonusFieldKey.AwardedBingoTickets,
            text: 'bonus.card.details_bingo_tickets_awarded',
            value: bonusAwarded,
            isCurrencyVisible: false,
        },
        {
            key: BonusFieldKey.BonusAmountGained,
            text: 'bonus.card.details_amount_gained',
            value: formatCurrency(bonusGainedAmount),
            isCurrencyVisible: true,
        },
        {
            key: BonusFieldKey.BonusAmountUsed,
            text: isFreeSpinBonus(type) ? 'bonus.card.details_spins_used' : 'bonus.card.details_amount_used',
            value: isFreeSpinBonus(type) ? bonusUsedAmount : formatCurrency(bonusUsedAmount),
            isCurrencyVisible: !isFreeSpinBonus(type),
        },
        {
            key: BonusFieldKey.AmountWon,
            text: 'bonus.card.amount_won',
            value: formatCurrency(redeemedAmount),
            isCurrencyVisible: true,
        },
        {
            key: BonusFieldKey.PotentialWin,
            text: 'bonus.card.details_potential_win',
            value: formatCurrency(bonusPotentialWin),
            isCurrencyVisible: true,
        },
    ].filter((item) => isDetailFieldVisible(item) && getBonusDetailKeys(type, bonusUsedAmount, redeemedAmount, bonusGainedAmount)?.includes(item.key));
}

function getBonusDetailKeys(
    type: BonusType,
    bonusUsedAmount: number,
    redeemedAmount: number,
    bonusGainedAmount: number): BonusFieldKey[] {
    switch (type) {
        case BonusType.Freebet:
            if (redeemedAmount > 0 || bonusUsedAmount > 0) {
                return [
                    BonusFieldKey.AvailableBonusAmount,
                    BonusFieldKey.BonusAmountUsed,
                    BonusFieldKey.AmountWon,
                ];
            } else {
                return [BonusFieldKey.AvailableBonusAmount];
            }
        case BonusType.Casino:
        case BonusType.Virtuals:
        case BonusType.CasinoVirtuals:
        case BonusType.Sport:
        case BonusType.LiveDealer:
            if (redeemedAmount > 0 || bonusGainedAmount > 0) {
                return [
                    BonusFieldKey.InitialAmount,
                    BonusFieldKey.BonusAmountGained,
                    BonusFieldKey.BonusAmountUsed,
                    BonusFieldKey.PotentialWin,
                ];
            } else if (bonusUsedAmount > 0) {
                return [
                    BonusFieldKey.InitialAmount,
                    BonusFieldKey.BonusAmountUsed,
                    BonusFieldKey.PotentialWin,
                ];
            } else {
                return [BonusFieldKey.PotentialWin];
            }
        case BonusType.FreeSpins:
            return [BonusFieldKey.AwardedFreeSpins];
        case BonusType.Bingo:
            return [BonusFieldKey.AwardedBingoTickets];
    }
    return [];
}

export function getBonusSportDescription(bonusConditions: BonusConditions): string {
    if (Object.keys(bonusConditions).length > 0) {
        if (isConditionWithinRange(bonusConditions.events)) {
            return formatDescriptionItems(bonusConditions.events);
        } else if (isConditionWithinRange(bonusConditions.tournaments)) {
            return formatDescriptionItems(bonusConditions.tournaments);
        } else if (isSportAndCategoryVisible(bonusConditions)) {
            return `${bonusConditions.sports![0].name}
                - ${bonusConditions.categories![0].name}`;
        } else if (bonusConditions.sports.length > 0) {
            return bonusConditions.sports!.length > MAX_DESCRIPTION_ITEMS_NUMBER
                ? 'bonus.card.details.multiple_sport'
                : formatDescriptionItems(bonusConditions.sports);
        }
    }

    return 'bonus.card.restriction.any_sport';
}

export function getBonusGamesDescription(bonusConditions: BonusConditions, isDescriptionDetailSection?: boolean): string {
    if (Object.keys(bonusConditions).length > 0) {
        if (isConditionWithinRange(bonusConditions.games)) {
            return formatDescriptionItems(bonusConditions.games);
        } else if (bonusConditions.games.length > 0 && bonusConditions.games!.length >= MAX_DESCRIPTION_ITEMS_NUMBER) {
            return isDescriptionDetailSection ? 'bonus.card.restriction.eligible_games' : 'bonus.card.details.multiple_game';
        } else if (bonusConditions.games.length > 0) {
            return formatDescriptionItems(bonusConditions.games);
        }
    }

    return isDescriptionDetailSection ? 'bonus.card.restriction.eligible_games' : 'bonus.card.restriction.any_game';
}

export function getComtradeBonusDescription(conditions: Condition[]): string {
    const bonusBetEligibility = conditions.find((item) => item.type === BonusCustomContentConditions.bonusBetEligibility);
    // Extract bolded part of bonus bet eligibility
    return bonusBetEligibility?.value?.substring(
        bonusBetEligibility.value.indexOf('<b>') + 3, bonusBetEligibility.value.indexOf('</b>'))
        ?? '';
}

export function getBonusDescriptionByType(bonusConditions: BonusConditions, type: BonusType): string {
    if (isSportOrFreeBetBonus(type)) {
        return getBonusSportDescription(bonusConditions);
    } else {
        return getBonusGamesDescription(bonusConditions);
    }
}

function isSportAndCategoryVisible(bonusConditions: BonusConditions) {
    return bonusConditions.sports?.length === 1 && bonusConditions.categories?.length === 1;
}

function isConditionWithinRange(conditions: ConditionDetail[]) {
    return conditions.length > 0 && conditions.length <= MAX_DESCRIPTION_ITEMS_NUMBER;
}

function getSportInfoDetails(bonusConditions: BonusConditions): Nullable<string> {
    if (Object.keys(bonusConditions).length > 0) {
        if (bonusConditions.events.length > MAX_DESCRIPTION_ITEMS_NUMBER) {
            return formatInfoDetails(bonusConditions.events.map((event) => event.name));
        } else if (bonusConditions.tournaments.length > MAX_DESCRIPTION_ITEMS_NUMBER) {
            return formatInfoDetails(bonusConditions.tournaments.map((tournament) => tournament.name));
        } else if (bonusConditions.sports.length > MAX_DESCRIPTION_ITEMS_NUMBER) {
            return formatInfoDetails(bonusConditions.sports.map((sport) => sport.name));
        }
    }

    return null;
}

function getGamesInfoDetails(bonusConditions: BonusConditions): Nullable<string> {
    if (Object.keys(bonusConditions).length > 0 && bonusConditions.games.length > MAX_DESCRIPTION_ITEMS_NUMBER) {
        return formatInfoDetails(bonusConditions.games.map((game) => game.name));
    } else {
        return null;
    }
}

function formatInfoDetails(detailsList: string[]): string {
    const dotSign = '\u00b7';
    return detailsList.map((item) => `${dotSign} ${item}`).join('<br>');
}

function formatDescriptionItems(conditionItem: ConditionDetail[]): string {
    return conditionItem
        .slice(0, MAX_DESCRIPTION_ITEMS_NUMBER)
        .map((item) => item.name)
        .join(', ');
}

function getBonusEventTypeRestriction(bonusEventType: BonusEventType) {
    switch (bonusEventType) {
        case BonusEventType.Live:
            return 'bonus.card.restriction.bet_on_live_events';
        case BonusEventType.PreMatch:
            return 'bonus.card.restriction.bet_on_prematch_events';
        case BonusEventType.Mixed:
            return 'bonus.card.restriction.bet_on_mixed_events';
    }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getBonusRequirements({
    bonusConditions,
    expirationDate,
    type,
    bonusSubtype,
    wageringRequirements,
    offerDetails,
}: BonusRequirementParams): BonusRequirement[] {
    const formattedExpirationDate = expirationDate
        ? `${Vue.prototype.$formatDateTime(expirationDate, 'ddmmyyyy')} - ${Vue.prototype.$formatTime(expirationDate, 'HHMM')} ${(getConfig().appLocale.locale !== Locale.EnglishBrazilian) ? 'h' : ''}`
        : null;

    return [
        hasOfferDetails(offerDetails) ? {
            key: BonusRequirementKey.OfferDetails,
            text: offerDetails,
        } : null,
        isSportOrFreeBetBonus(type) ? {
            key: BonusRequirementKey.SportEvents,
            text: 'bonus.details.bet_on',
            condition: getBonusSportDescription(bonusConditions),
            bonusAdditionalInfo: getSportInfoDetails(bonusConditions),
        } : null,
        isCasinoBonus(type) || isFreeSpinBonus(type) ? {
            key: BonusRequirementKey.CasinoGames,
            text: 'bonus.details.play_game',
            condition: getBonusGamesDescription(bonusConditions, true),
            bonusAdditionalInfo: getGamesInfoDetails(bonusConditions),
        } : null,
        isWageringBonus(bonusSubtype) && wageringRequirements > 0 ? {
            key: BonusRequirementKey.WageringRequrement,
            text: isSportOrFreeBetBonus(type) ? 'bonus.card.restriction.bet_total_of' : 'bonus.card.restriction.play_total_of',
            condition: Vue.prototype.$twoDecimals(wageringRequirements),
            isCurrency: true,
        } : null,
        bonusConditions?.minEventOdd ? {
            key: BonusRequirementKey.MinEventOdd,
            text: 'bonus.card.restriction.min_event_odd',
            condition: bonusConditions.minEventOdd.toFixed(2),
        } : null,
        bonusConditions?.minTotalOdd ? {
            key: BonusRequirementKey.MinTotalOdd,
            text: 'bonus.card.restriction.min_betslip_odd',
            condition: bonusConditions.minTotalOdd.toFixed(2),
        } : null,
        bonusConditions?.bonusEventType ? {
            key: BonusRequirementKey.BonusEventType,
            text: getBonusEventTypeRestriction(bonusConditions.bonusEventType),
        } : null,
        bonusConditions?.bonusTicketType ? {
            key: BonusRequirementKey.BonusTicketType,
            text: bonusConditions.bonusTicketType === BonusTicketType.Single ? 'bonus.card.restriction.bet_on_single_bets' : 'bonus.card.restriction.bet_on_accumulator_bets',
        } : null,
        isSportWageringBonus(type) ? {
            key: BonusRequirementKey.BetsMustSettle,
            text: 'bonus.card.restriction.bets_must_settle',
        } : null,
        formattedExpirationDate ? {
            key: BonusRequirementKey.ExpirationDate,
            text: 'bonus.card.expires_on',
            condition: formattedExpirationDate,
        } : null,
    ].filter((item) => !!item) as BonusRequirement[];
}

function hasOfferDetails(offerDetails: string | undefined): boolean {
    return (offerDetails ?? '').trim().length > 0;
}

export function isComtradeBonus(kind: BonusKind): boolean {
    return kind === BonusKind.Comtrade;
}

export function isBetlerBonus(kind: BonusKind): boolean {
    return kind === BonusKind.Betler;
}

export function getBonusDescriptionTerms(description: string | null): BonusDescription {
    if (!description) {
        return {};
    }

    const DESCRIPTION_SEPARATOR = '|';
    if (!description.includes(DESCRIPTION_SEPARATOR)) {
        return {
            text: description,
        };
    }

    const [text, termsAndConditionsUrl] = description.split(DESCRIPTION_SEPARATOR).map((s) => s.trim());
    return {
        text: text || undefined,
        termsAndConditionsUrl: termsAndConditionsUrl || undefined,
    };
}

export function getBetlerBonusSummary(
    bonusType: PromotionBonusType | null,
    bonusAmount = 0,
    bonusRewardType?: string,
    isReward = false,
) {
    if (!bonusType) {
        return;
    }

    const { translate, translatePluralized, formatCurrency } = useI18n();
    const rewardLabel = [
        isReward ? 'reward' : 'bonus_value',
        bonusRewardType === 'dynamic' && 'dynamic',
    ].filter(Boolean).join('.');

    let translationKey = '';
    switch (bonusType) {
        case PromotionBonusType.Honey:
            return translate('promotion.card.honey_title');
        case PromotionBonusType.External:
            translationKey = translatePluralized(translationPlaceholderFormat(translate('bonus.free_spins'), {
                bonusAmount: bonusAmount.toString(),
            }), bonusAmount);

            return translationPlaceholderFormat(translate(`promotion.card.${rewardLabel}.free_spins`), {
                bonusAmount: translationKey,
                currency: translate('currencyAbbreviation'),
            });
        case PromotionBonusType.FreeBet:
            translationKey = `promotion.card.${rewardLabel}.free_bet_bonus`;
            break;
        case PromotionBonusType.Sportsbook:
            translationKey = `promotion.card.${rewardLabel}.sport_bonus`;
            break;
        case PromotionBonusType.Casino:
            translationKey = `promotion.card.${rewardLabel}.casino_bonus`;
            break;
    }

    return translationPlaceholderFormat(translate(translationKey!), {
        bonusAmount: formatCurrency(bonusAmount),
        currency: translate('currencyAbbreviation'),
    });
}

export function getBonusAmountAndCurrency(bonusAmount: string | number, isCurrencyVisible?: boolean): string {
    const { translate } = useI18n();

    return translationPlaceholderFormat(translate('bonus_comms.common.amount_and_currency'), {
        bonusAmount: bonusAmount.toString(),
        currency: isCurrencyVisible ? translate('currencyAbbreviation') : '',
    });
}

export function getBonusTermsAndConditionsUrl(termsUrl: string | undefined) {
    return termsUrl && isValidUrl(termsUrl) ? termsUrl : undefined;
}
